import { useEffect, useState } from "react";
import { Link, useSearch } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { AuthBrandHeader } from "@/components/AuthBrandHeader";
import { useToast } from "@/hooks/use-toast";
import { FONT, SAGE } from "@shared/brand";
import { ArrowLeft } from "lucide-react";

/**
 * Email-verification interstitial (s90). Shown after signup and when login is blocked
 * for an unverified account. Accessible unauthenticated (hard-block model: an unverified
 * signup never gets a session). The email is resolved from the ?email query param
 * (set by the signup/login redirects and expired-token bounce) or localStorage.
 */
export default function VerifyEmail() {
  const search = useSearch();
  const { toast } = useToast();
  const params = new URLSearchParams(search);
  const expired = params.get("expired") === "true";

  const [email] = useState<string>(() => {
    const fromQuery = params.get("email");
    if (fromQuery) return fromQuery;
    try { return localStorage.getItem("pendingVerificationEmail") ?? ""; } catch { return ""; }
  });

  const [sending, setSending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [sentOnce, setSentOnce] = useState(false);

  // Persist the resolved email so a refresh keeps the resend target.
  useEffect(() => {
    if (email) { try { localStorage.setItem("pendingVerificationEmail", email); } catch {} }
  }, [email]);

  // Countdown ticker for the 60s resend cooldown.
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const handleResend = async () => {
    if (!email) {
      toast({ title: "No email on file", description: "Please sign in again to resend.", variant: "destructive" });
      return;
    }
    setSending(true);
    try {
      // Raw fetch (not apiRequest) so a 429 doesn't throw before we can read it.
      const res = await fetch("/api/auth/resend-verification", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
        credentials: "include",
      });
      if (res.status === 429) {
        const err = await res.json().catch(() => ({}));
        toast({ title: "Please wait", description: err.error || "Too many requests. Try again later.", variant: "destructive" });
        return;
      }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Failed to resend");
      }
      setSentOnce(true);
      setCooldown(60);
      toast({ title: "Verification email sent!", description: "Check your inbox." });
    } catch (e: any) {
      toast({ title: "Could not resend", description: e.message || "Please try again.", variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen p-4">
      <div className="flex flex-col items-center w-full max-w-md">
        <div className="mb-3">
          <Link href="/">
            <span className="text-sm text-muted-foreground hover:text-primary cursor-pointer inline-flex items-center gap-1" data-testid="link-back-home"><ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />Back to home</span>
          </Link>
        </div>
        <AuthBrandHeader />
        {expired && (
          <div
            className="w-full mb-4 rounded-md px-4 py-3 text-sm"
            style={{ backgroundColor: "#FDECEA", color: "#9B2C2C" }}
            data-testid="text-link-expired"
          >
            Your verification link has expired. Request a new one below.
          </div>
        )}
        <Card className="w-full">
          <CardHeader>
            <CardTitle style={{ fontFamily: FONT.serif }}>Check your email</CardTitle>
            <CardDescription data-testid="text-verify-description">
              {email ? (
                <>We've sent a verification link to <strong>{email}</strong>. Click the link in the email to verify your account.</>
              ) : (
                <>We've sent you a verification link. Click the link in the email to verify your account.</>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">Didn't receive it?</p>
            <Button
              type="button"
              className="w-full"
              onClick={handleResend}
              disabled={sending || cooldown > 0}
              data-testid="button-resend-verification"
            >
              {sending ? "Sending..." : cooldown > 0 ? `Resend in ${cooldown}s` : "Resend verification email"}
            </Button>
            {sentOnce && cooldown > 0 && (
              <p className="text-xs text-center" style={{ color: SAGE[600] }} data-testid="text-resend-success">
                Verification email sent!
              </p>
            )}
            <div className="text-center text-sm">
              <Link href="/login">
                <span className="text-primary hover:underline cursor-pointer" data-testid="link-back-to-login">
                  Back to sign in
                </span>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
